/**
 * share-card.ts — Share card capture + Telegram share + download fallback.
 *
 * T23: Moon Phase Soulmate share-card generator.
 *
 * BLOCKERS.md B1: WebApp.shareToStory may not be available in Telegram <v7.8.
 * Resolution: shareViaTelegram returns false when unavailable → caller invokes
 * downloadCapture as fallback.
 */

// html-to-image is imported dynamically in captureShareCard() so the library
// stays out of the initial bundle (only loaded when a share card is captured).

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export interface ShareCardCapture {
  /** data:image/png;base64,... */
  dataUrl: string;
  /** Blob for download fallback */
  blob: Blob;
  /** Suggested filename */
  filename: string;
}

// --------------------------------------------------------------------------
// Internal helpers
// --------------------------------------------------------------------------

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

// --------------------------------------------------------------------------
// captureShareCard
// --------------------------------------------------------------------------

/**
 * Renders `node` (1080×1920) to a PNG data URL + Blob.
 * The node must already be laid out at real pixel dimensions in the DOM
 * (off-screen via position:fixed; left:-99999px is fine).
 */
export async function captureShareCard(node: HTMLElement): Promise<ShareCardCapture> {
  const { toPng } = await import('html-to-image');
  const dataUrl = await toPng(node, {
    width: 1080,
    height: 1920,
    pixelRatio: 1,       // card is already at native res
    cacheBust: true,
    backgroundColor: '#05030f', // --void
  });

  const blob = dataUrlToBlob(dataUrl);
  const filename = `calestea-moon-phase-${new Date().toISOString().slice(0, 10)}.png`;

  return { dataUrl, blob, filename };
}

// Only the faces the share card actually uses (Spectral 400/400i/500i/600i +
// IBM Plex Mono 400). Requested narrowly so we inline as few woff2 files as
// possible. These are already browser-cached from index.html's <link>.
const SHARE_FONTS_CSS_URL =
  'https://fonts.googleapis.com/css2?family=Spectral:ital,wght@0,400;1,400;1,500;1,600&family=IBM+Plex+Mono:wght@400&display=swap';

function arrayBufferToBase64(buf: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

let cachedFontEmbedCSS: string | undefined | null = null;

/**
 * Build @font-face CSS with the woff2 files inlined as data: URIs.
 *
 * html-to-image cannot embed our web fonts on its own: they come from the
 * cross-origin Google Fonts stylesheet (fonts.googleapis.com), whose @font-face
 * rules are unreadable via CSSOM (CORS), so the snapshot falls back to Georgia
 * and the tight line-heights collapse into overlapping text. We sidestep that by
 * fetching the CSS ourselves (CORS-open) + inlining each gstatic woff2, then
 * handing the result to html-to-image via its `fontEmbedCSS` option. Cached
 * after the first build. Returns undefined on failure (capture proceeds; the
 * line-height safety net keeps it from overlapping badly).
 */
async function getShareFontEmbedCSS(): Promise<string | undefined> {
  if (cachedFontEmbedCSS !== null) return cachedFontEmbedCSS ?? undefined;
  try {
    const cssText = await (await fetch(SHARE_FONTS_CSS_URL)).text();
    const urlRe = /url\((https:\/\/[^)'"]+\.woff2)\)/g;
    const urls = [...new Set([...cssText.matchAll(urlRe)].map((m) => m[1]))];
    const map = new Map<string, string>();
    await Promise.all(
      urls.map(async (u) => {
        const ab = await (await fetch(u)).arrayBuffer();
        map.set(u, `data:font/woff2;base64,${arrayBufferToBase64(ab)}`);
      }),
    );
    cachedFontEmbedCSS = cssText.replace(urlRe, (_m, u: string) => `url(${map.get(u) ?? u})`);
    return cachedFontEmbedCSS;
  } catch {
    cachedFontEmbedCSS = undefined;
    return undefined;
  }
}

/**
 * Renders `node` (1080×1920) to a JPEG data URL. Telegram's
 * InlineQueryResultPhoto (used by the "share to chat" prepared message) requires
 * JPEG, and JPEG keeps the upload comfortably under the 5 MB photo cap.
 */
export async function captureShareCardJpeg(node: HTMLElement): Promise<string> {
  const { toJpeg } = await import('html-to-image');
  try {
    await document.fonts?.ready;
  } catch {
    /* fonts API unavailable */
  }
  const fontEmbedCSS = await getShareFontEmbedCSS();
  return toJpeg(node, {
    width: 1080,
    height: 1920,
    pixelRatio: 1,
    backgroundColor: '#05030f', // --void
    quality: 0.92,
    // Provide the inlined fonts so the snapshot renders with real Spectral
    // metrics (no Georgia fallback → no overlap). Omitted only if the fetch
    // failed, in which case html-to-image uses its (font-less) default.
    ...(fontEmbedCSS ? { fontEmbedCSS } : {}),
  });
}

// --------------------------------------------------------------------------
// shareViaTelegram
// --------------------------------------------------------------------------

/**
 * Attempts to share via Telegram WebApp.shareToStory (requires v7.8+).
 * Returns true if the call was dispatched, false if unavailable or it threw.
 */
export async function shareViaTelegram(capture: ShareCardCapture): Promise<boolean> {
  const wa = (window as unknown as { Telegram?: { WebApp?: { shareToStory?: unknown } } })
    .Telegram?.WebApp;

  if (wa?.shareToStory && typeof wa.shareToStory === 'function') {
    try {
      (wa.shareToStory as (url: string, opts: { text: string }) => void)(
        capture.dataUrl,
        { text: 'Moon Phase Soulmate · calestea.com' },
      );
      return true;
    } catch {
      return false;
    }
  }

  return false;
}

// --------------------------------------------------------------------------
// downloadCapture
// --------------------------------------------------------------------------

/**
 * Triggers a browser download of the capture PNG.
 * Used as fallback when Telegram shareToStory is unavailable (BLOCKERS.md B1).
 */
export function downloadCapture(capture: ShareCardCapture): void {
  const a = document.createElement('a');
  a.href = capture.dataUrl;
  a.download = capture.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
